// Fase 6A + Fix Billing + Trial: Context central de suscripción.
// El plan funcional se determina desde workspaceMetadata server-side.
// El trial solo puede conceder PRO dentro del workspace personal.
// billingPlan local se conserva para UX, pero NO concede permisos.

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { PLANS, getPermissionsForPlan } from '@/config/subscriptionPlans';
import { db } from '@/firebase';
import { onSnapshot, doc, collection } from 'firebase/firestore';
import { useAuth } from '@/context/AuthContext';
import {
  initBillingClient,
  querySubscriptionProducts,
  addPurchaseListener,
  addResumeListener,
  mapPurchaseToPlan,
  verifyPurchaseWithBackend,
  verifyPendingPurchase,
} from '@/services/billingService';
import { getTrialStatus } from '@/services/trialService';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);

  if (!ctx) {
    throw new Error('useSubscription debe usarse dentro de <SubscriptionProvider>');
  }

  return ctx;
}

export function SubscriptionProvider({ children, workspaceId }) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  // Refs para verificación puntual de compra tras resume de Google Play.
  // Se guardan aquí porque SubscriptionProvider es el único lugar con ciclo de vida
  // que cubre toda la sesión de la app (los modales se desmontan/remontan).
  const pendingPurchaseProductIdRef = useRef(null);
  const verifyingPendingPurchaseRef = useRef(false);

  // Plan funcional actual.
  const [plan, _setPlan] = useState(PLANS.FREE);

  const [loadingSubscription] = useState(false);
  const [cachedProducts, setCachedProducts] = useState([]);

  // Plan detectado localmente por Google Play.
  // Se conserva para UX, pero NO concede permisos.
  const [billingPlan, setBillingPlan] = useState(null);

  // Número de perfiles contratados realmente en Google Play.
  // Es información comercial/UX y NO determina por sí sola los permisos.
  const [purchasedMaxProfiles, setPurchasedMaxProfiles] = useState(null);

  // Plan efectivo server-side del workspace actualmente abierto.
  const [workspaceMetadataPlan, setWorkspaceMetadataPlan] = useState(null);
  const [workspaceMaxProfiles, setWorkspaceMaxProfiles] = useState(null);

  // Entitlement interno del usuario.
  const [entitlementPlan, setEntitlementPlan] = useState(null);

  const [isPlanReady, setIsPlanReady] = useState(false);

  // null = no iniciado
  // true = Billing disponible
  // false = Billing no disponible
  const [billingAvailable, setBillingAvailable] = useState(null);

  // Trial Pro 60 días.
  const [trialPlan, setTrialPlan] = useState(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);

  // Testing manual desde DevTools.
  const setPlan = (newPlan) => {
    _setPlan(newPlan);
  };

  // ── Calcular plan efectivo ──
  //
  // Autoridad funcional:
  //
  // 1. workspaceMetadata del workspace actual.
  // 2. Trial, únicamente si el usuario está en su workspace personal.
  // 3. FREE.
  //
  // billingPlan local NO concede permisos.
  useEffect(() => {
    let effectivePlan;

    if (workspaceMetadataPlan === PLANS.PREMIUM_PRO) {
      effectivePlan = PLANS.PREMIUM_PRO;
    } else if (workspaceMetadataPlan === PLANS.PREMIUM_PERSONAL) {
      effectivePlan = PLANS.PREMIUM_PERSONAL;
    } else if (
  trialPlan === PLANS.PREMIUM_PRO
) {
      effectivePlan = PLANS.PREMIUM_PRO;
    } else {
      effectivePlan = PLANS.FREE;
    }

    _setPlan(effectivePlan);
  }, [
    workspaceMetadataPlan,
    trialPlan,
    uid,
    workspaceId,
  ]);

  // ── Inicialización de Billing ──
  //
  // Se ejecuta una sola vez durante la vida de la app.
  // Billing es global al dispositivo y NO está scoped al usuario Firebase.
  useEffect(() => {
    async function initBilling() {
      const result = await initBillingClient();

      if (!result.connected) {
        console.warn(
          '[BILLING] BillingClient no conectado — Billing no disponible'
        );
        setBillingAvailable(false);
        return;
      }

      setBillingAvailable(true);

      // Cachear productos para PlanUpgradeModal.
      const products = await querySubscriptionProducts();
      setCachedProducts(products);

      // No consultar compras activas automáticamente al arrancar.
      //
      // Google Play Billing devuelve compras de la cuenta Google
      // del dispositivo y no del usuario Firebase.
      //
      // La autoridad funcional está ahora en Firestore:
      // workspaceMetadata/{workspaceId}.effectivePlan.

      // Listener para compras completadas en tiempo real.
      addPurchaseListener(async (data) => {
        // Manejo de USER_CANCELED: limpiar pendiente sin conceder nada.
        if (data.responseCode === 1) {
          console.log('[BILLING] User canceled purchase');
          pendingPurchaseProductIdRef.current = null;
          return;
        }

        if (
          data.responseCode === 0 &&
          data.purchases?.length > 0
        ) {
          for (const purchase of data.purchases) {
            const newPlan = mapPurchaseToPlan(purchase);

            if (newPlan && purchase.purchaseToken) {
              try {
                // Verificar primero la compra en backend.
                await verifyPurchaseWithBackend(
                  purchase.purchaseToken
                );

                console.log(
                  '[BILLING] Purchase verified with backend, awaiting server entitlement...'
                );

                // billingPlan se mantiene únicamente para UX.
                // NO participa en permissions ni en el plan funcional.
                setBillingPlan(newPlan);

                // Limpiar pendiente para evitar doble verificación desde resume.
                pendingPurchaseProductIdRef.current = null;
              } catch (error) {
                console.warn(
                  '[BILLING] Purchase verification failed, not accepting locally:',
                  error
                );
              }

              break;
            }
          }
        }
      });

      // Listener para vuelta a foreground (p. ej. tras volver de Google Play checkout).
      // Se ejecuta si purchasesUpdated no procesó la compra correctamente.
      addResumeListener(async () => {
        const productId = pendingPurchaseProductIdRef.current;
        if (!productId || verifyingPendingPurchaseRef.current) {
          return;
        }

        verifyingPendingPurchaseRef.current = true;

        try {
          // Primer intento: verificar inmediatamente.
          let plan = await verifyPendingPurchase(productId);

          // Si no se encontró compra válida, esperar brevemente (Google Play
          // puede tardar fracciones de segundo en reflejar la transacción)
          // e intentar una sola vez más.
          if (!plan) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            plan = await verifyPendingPurchase(productId);
          }

          if (plan) {
            console.log('[BILLING] Resume: Purchase verified for', productId);
            setBillingPlan(plan);
            pendingPurchaseProductIdRef.current = null;
          } else {
            // Silencioso: la compra simplemente no está lista o el usuario
            // canceló. Limpiar el pendiente para no dejar estado huérfano.
            console.warn(
              '[BILLING] Resume: No valid purchase found for',
              productId
            );
            pendingPurchaseProductIdRef.current = null;
          }
        } catch (error) {
          console.warn('[BILLING] Resume verification error:', error);
          pendingPurchaseProductIdRef.current = null;
        } finally {
          verifyingPendingPurchaseRef.current = false;
        }
      });
    }

    initBilling();
  }, []);

  // ── Internal entitlement + Trial ──
  //
  // Dependientes del UID Firebase.
  useEffect(() => {
    let cancelled = false;

    // Limpiar estado del usuario anterior.
    setEntitlementPlan(null);
    setTrialPlan(null);
    setTrialExpiresAt(null);
    setTrialDaysRemaining(0);
    setIsPlanReady(false);

    if (!uid || !workspaceId) {
      setIsPlanReady(true);
      return;
    }

    let entitlementResolved = false;
    let trialResolved = false;

    const maybeReady = () => {
      if (
        entitlementResolved &&
        trialResolved &&
        !cancelled
      ) {
        setIsPlanReady(true);
      }
    };

    // Trial.
    getTrialStatus(workspaceId)
      .then((trialStatus) => {
        if (cancelled) return;

        if (trialStatus.hasActiveTrial) {
          setTrialPlan(PLANS.PREMIUM_PRO);
          setTrialExpiresAt(
            trialStatus.trialExpiresAt
          );
          setTrialDaysRemaining(
            trialStatus.daysRemaining
          );
        }

        trialResolved = true;
        maybeReady();
      })
      .catch((trialErr) => {
        if (cancelled) return;

        console.warn(
          '[TRIAL] Error checking trial status:',
          trialErr?.message
        );

        trialResolved = true;
        maybeReady();
      });

    // Internal entitlement.
    const unsubscribe = onSnapshot(
      doc(
        collection(db, 'internalEntitlements'),
        uid
      ),
      (docSnap) => {
        if (cancelled) return;

        if (docSnap.exists()) {
          const data = docSnap.data();

          setEntitlementPlan(
            data.active === true &&
            data.plan === PLANS.PREMIUM_PRO
              ? PLANS.PREMIUM_PRO
              : null
          );
        } else {
          setEntitlementPlan(null);
        }

        entitlementResolved = true;
        maybeReady();
      },
      (error) => {
        if (cancelled) return;

        console.error(
          '[ENTITLEMENT] Error loading internal entitlement:',
          error
        );
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [uid, workspaceId]);

  // ── Google Play entitlement del usuario ──
  //
  // Se usa para conocer la suscripción comercial contratada.
  // No sustituye a workspaceMetadata como autoridad funcional.
  useEffect(() => {
    let cancelled = false;

    setPurchasedMaxProfiles(null);

    if (!uid) {
      return;
    }

    const unsubscribe = onSnapshot(
      doc(
        collection(db, 'googlePlayEntitlements'),
        uid
      ),
      (docSnap) => {
        if (cancelled) return;

        if (!docSnap.exists()) {
          setPurchasedMaxProfiles(null);
          return;
        }

        const data = docSnap.data();

        const maxProfiles =
          data.active === true &&
          Number.isInteger(data.maxProfiles)
            ? data.maxProfiles
            : null;

        setPurchasedMaxProfiles(maxProfiles);
      },
      (error) => {
        if (cancelled) return;

        console.error(
          '[GOOGLE_PLAY_ENTITLEMENT] Error loading Google Play entitlement:',
          error
        );

        setPurchasedMaxProfiles(null);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [uid]);

  // ── WorkspaceMetadata ──
  //
  // Esta es la fuente server-authoritative del plan funcional
  // del workspace actualmente abierto.
  //
  // Si workspaceId === uid:
  //   estamos en el workspace personal.
  //
  // Si workspaceId !== uid:
  //   estamos en un workspace externo y solo aplica el plan
  //   de ese workspace.
  useEffect(() => {
    let cancelled = false;

    setWorkspaceMetadataPlan(null);
    setWorkspaceMaxProfiles(null);

    if (!workspaceId) {
      return;
    }

    const unsubscribe = onSnapshot(
      doc(
        collection(db, 'workspaceMetadata'),
        workspaceId
      ),
      (docSnap) => {
        if (cancelled) return;

        if (docSnap.exists()) {
          const data = docSnap.data();
          const effectivePlan = data.effectivePlan;
          const maxProfiles = Number.isInteger(data.maxProfiles)
            ? data.maxProfiles
            : null;

          setWorkspaceMaxProfiles(maxProfiles);

          if (effectivePlan === 'PREMIUM_PRO') {
            setWorkspaceMetadataPlan(
              PLANS.PREMIUM_PRO
            );
          } else if (
            effectivePlan === 'PREMIUM_PERSONAL'
          ) {
            setWorkspaceMetadataPlan(
              PLANS.PREMIUM_PERSONAL
            );
          } else {
            setWorkspaceMetadataPlan(null);
          }
        } else {
          setWorkspaceMetadataPlan(null);
          setWorkspaceMaxProfiles(null);
        }
      },
      (error) => {
        if (cancelled) return;

        console.error(
          '[WORKSPACE_METADATA] Error loading workspace metadata:',
          error
        );

        setWorkspaceMetadataPlan(null);
        setWorkspaceMaxProfiles(null);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [workspaceId]);

  // ── Restaurar compras ──
  //
  // Acción manual de recuperación.
  // billingService verifica primero la compra contra backend.
  const restorePurchases = async () => {
    const {
      restorePurchases: billingRestore,
    } = await import(
      '@/services/billingService'
    );

    const restoredPlan = await billingRestore();

    // Solo se conserva para UX local.
    // No concede permisos.
    setBillingPlan(restoredPlan);

    return restoredPlan;
  };

  // Los permisos dependen exclusivamente del plan funcional
  // calculado arriba.
  const permissions = getPermissionsForPlan(plan);

  const value = {
    plan,

    isFree:
      plan === PLANS.FREE,

    isPremiumPersonal:
      plan === PLANS.PREMIUM_PERSONAL,

    isPremiumPro:
      plan === PLANS.PREMIUM_PRO,

    billingPlan,
    loadingSubscription,

    // Solo para testing interno.
    setPlan,

    permissions,
    maxProfiles: workspaceMaxProfiles,
    purchasedMaxProfiles,

    cachedProducts,

    restorePurchases,

    // Registrar el productId que se acaba de intentar comprar,
    // para verificación puntual tras volver del checkout.
    registerPendingPurchase: (productId) => {
      pendingPurchaseProductIdRef.current = productId;
    },

    isPlanReady,

    billingAvailable,

    trialPlan,
    trialExpiresAt,
    trialDaysRemaining,

    // Se mantiene por compatibilidad con consumidores actuales.
    entitlementPlan,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
